<template>
  <div class="task-session card" :style="{ width: '100%', maxWidth: resolvedWidth }" @click="emit('card-click')">
    <div class="task-session__header">
      <div class="left">
        <input
          class="header-checkbox"
          type="checkbox"
          :checked="checked"
          @click.stop
          @change="onCheckboxChange"
        >
        <UserIcon
          :user="avatarUser"
          :size="'24px'"
        />
        <EncodedIdField
          v-if="showUid"
          :id="numericId"
          :type="uidType"
          :hideLabel="true"
        />
        <span v-if="title" class="header-title">{{ title }}</span>
        <slot name="header" />
      </div>
      <div class="right">
        <slot name="header-right-before-delete" />
        <IconButton
          v-if="showDelete"
          class="header-delete-icon"
          :icon="'delete'"
          :width="24"
          :height="24"
          @click.stop="onDeleteClick"
        />
        <slot name="header-right" />
      </div>
    </div>

    <div class="body">
      <div v-if="showDataSection" class="body__section body__section--data">
        <div class="body__grid">
          <div class="statistics-field" v-for="entry in dataEntries" :key="entry.key">
            <div class="description">{{ entry.key }}</div>
            <div class="value">{{ entry.value }}</div>
          </div>
        </div>
      </div>

      <hr v-if="showDataSection && (showSlotSection || showDescriptionSection)" class="body__divider">

      <div v-if="showSlotSection" class="body__section body__section--slot">
        <slot name="body" />
      </div>

      <hr v-if="showSlotSection && showDescriptionSection" class="body__divider">

      <div v-if="showDescriptionSection" class="body__section body__section--description">
        <DescriptionField
          class="body__description"
          :text="description"
          :clickable="descriptionClickable"
          @description-changed="emit('description-changed', $event)"
        />
      </div>
    </div>

    <div class="footer">
      <slot name="footer">
        <span v-if="humanReadableFooterDate">Created: {{ humanReadableFooterDate }}</span>
      </slot>
    </div>
  </div>
</template>

<script setup>
import { computed, useSlots } from 'vue';
import EncodedIdField from '@/components/EncodedIdField.vue';
import UserIcon from '@/components/UserIcon.vue';
import DescriptionField from '@/components/Cards/DescriptionField.vue';
import IconButton from '@/components/IconButton.vue';

const slots = useSlots();

const props = defineProps({
  id: {
    type: [Number, String],
    default: null,
  },
  username: {
    type: String,
    default: '',
  },
  state: {
    type: String,
    default: '',
  },
  title: {
    type: String,
    default: '',
  },
  data: {
    type: Object,
    default: () => ({}),
  },
  description: {
    type: String,
    default: '',
  },
  descriptionClickable: {
    type: Boolean,
    default: true,
  },
  checked: {
    type: Boolean,
    default: false,
  },
  width: {
    type: [String, Number],
    default: '300px',
  },
  uidType: {
    type: String,
    default: 'task',
  },
  showUid: {
    type: Boolean,
    default: true,
  },
  footerDate: {
    type: [String, Number, Date],
    default: '',
  },
  showDelete: {
    type: Boolean,
    default: false,
  },
  deleteConfirmMessage: {
    type: String,
    default: 'Are you sure you want to delete this item?',
  },
});

const emit = defineEmits(['card-click', 'description-changed', 'update:checked', 'checkbox-change', 'delete-click']);

const hasData = computed(() => (
  props.data
  && typeof props.data === 'object'
  && !Array.isArray(props.data)
  && Object.keys(props.data).length > 0
));

const dataEntries = computed(() => {
  if (!hasData.value) {
    return [];
  }

  return Object.entries(props.data).map(([key, value]) => {
    const asText = typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean'
      ? String(value)
      : JSON.stringify(value);
    const looksLikeDateField = /date|time|created_at|createdat|created_date/i.test(String(key));

    return {
      key,
      value: looksLikeDateField ? formatDateForDisplay(asText) : asText,
    };
  });
});

const showDataSection = computed(() => hasData.value);

const showSlotSection = computed(() => !!slots.body);

const showDescriptionSection = computed(() => String(props.description || '').trim() !== '');

const resolvedWidth = computed(() => {
  if (props.width === 'full') {
    return '100%';
  }

  if (typeof props.width === 'number') {
    return `${props.width}px`;
  }

  const widthText = String(props.width || '').trim();
  if (/^\d+$/.test(widthText)) {
    return `${widthText}px`;
  }
  if (/^\d+px$/i.test(widthText)) {
    return widthText;
  }

  return '300px';
});

const numericId = computed(() => {
  const parsed = Number(props.id);
  return Number.isFinite(parsed) ? parsed : 0;
});

const avatarUser = computed(() => ({
  username: props.username || '',
}));

function formatDateForDisplay(rawValue) {
  const textValue = String(rawValue || '').trim();
  if (!textValue || textValue.startsWith('0001-01-01')) {
    return '';
  }

  const parsed = new Date(textValue);
  if (Number.isNaN(parsed.getTime())) {
    return textValue;
  }

  const year = parsed.getFullYear();
  const month = String(parsed.getMonth() + 1).padStart(2, '0');
  const day = String(parsed.getDate()).padStart(2, '0');
  const hours24 = parsed.getHours();
  const minutes = String(parsed.getMinutes()).padStart(2, '0');
  const meridiem = hours24 >= 12 ? 'pm' : 'am';
  const hours12 = hours24 % 12 || 12;
  const hour = String(hours12).padStart(2, '0');

  return `${year}-${month}-${day} ${hour}:${minutes} ${meridiem}`;
}

const humanReadableFooterDate = computed(() => {
  return formatDateForDisplay(props.footerDate);
});

function onCheckboxChange(event) {
  const value = !!event.target?.checked;
  emit('update:checked', value);
  emit('checkbox-change', value);
}

function onDeleteClick() {
  emit('delete-click');
}
</script>

<style lang="scss" scoped>
.card {
  display: flex;
  flex-direction: column;
  position: relative;
  height: fit-content;
  margin: 0;
  text-align: left;
  border-radius: 8px;
  box-shadow: 1px 1px 4px 1px rgba(0, 0, 0, 0.2), 0 0 1px 1px rgba(0, 0, 0, 0.05);
}

.task-session {
  display: flex;
  flex-direction: column;
  border-radius: 8px;
  min-height: 220px;
  max-width: 100%;
  padding: 0;
  cursor: pointer;

  &__header {
    display: flex;
    flex-direction: row;
    align-items: center;
    padding: 4px 10px;
    background: var(--gray-200);
    height: 32px;
  }

  .left {
    width: 50%;
    min-width: 50%;
    display: flex;
    flex-direction: row;
    align-items: center;
    justify-content: flex-start;
    gap: 8px;
  }

  .right {
    width: 50%;
    min-width: 50%;
    display: flex;
    flex-direction: row;
    justify-content: flex-end;
  }
}

.header-checkbox {
  width: 14px;
  height: 14px;
}

.header-title {
  font-size: 0.85rem;
  font-weight: 600;
  color: var(--body-text-color-secondary);
}

.header-delete-icon {
  color: #974b4b;
}

.body {
  position: relative;
  margin-top: 0;
  display: flex;
  flex-direction: row;
  flex-wrap: wrap;
  row-gap: 0;
  column-gap: 10px;
  width: 100%;
  flex: 1 1 auto;
  min-height: 0;
  overflow: hidden;

  &__section {
    flex: 1 1 240px;
    min-width: 240px;
    box-sizing: border-box;
  }

  &__divider {
    width: 100%;
    margin-top: 8px;
    border: 0;
    border-top: 1px solid #b4b4b4;
  }

  &__section--data {
    padding: 0;
  }

  &__grid {
    display: block;
    margin: 0;
    padding: 0;

    .statistics-field {
      display: flex;
      flex-direction: row;
      margin-top: 2px;
    }

    .description {
      font-size: 0.8rem;
      font-weight: 600;
      color: var(--body-text-color);
      width: 72px;
      min-width: 72px;
      text-transform: capitalize;
    }

    .value {
      font-size: 0.8rem;
      font-weight: 600;
      color: var(--body-text-color-secondary);
      overflow: hidden;
      white-space: nowrap;
      text-overflow: ellipsis;
    }
  }

  &__description {
    border-top: none;
    padding-top: 0;
    height: 80px;
  }
}

.footer {
  display: flex;
  flex-direction: row;
  width: 100%;
  height: fit-content;
  padding: 4px;
  padding-left: 10px;
  font-size: 0.62rem;
  color: #4a4a4a;
}

:slotted(.header-title) {
  font-size: 0.85rem;
  font-weight: 600;
  color: var(--body-text-color-secondary);
}

:slotted(.footer-date) {
  font-size: 0.62rem;
  font-weight: 400;
  color: #4a4a4a;
}
</style>
